import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import i18n from "@lib/i18n/i18n";
import { SUPPORTED_LANGUAGES, isRtl } from "@lib/i18n/i18n";

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

interface LanguageStore {
    language: LanguageCode;
    setLanguage: (code: LanguageCode) => void;
}

const PERSIST_KEY = "maeiz-rider-language-store";
const I18N_LANG_KEY = "maeiz-rider-lang";

const getInitialLanguage = (): LanguageCode => {
    if (typeof window !== "undefined") {
        const stored = localStorage.getItem(I18N_LANG_KEY);
        const valid = SUPPORTED_LANGUAGES.some((l) => l.code === stored);
        if (stored && valid) return stored as LanguageCode;
        const fromI18n = (typeof i18n !== "undefined" && i18n.language) || "";
        const i18nMatch = SUPPORTED_LANGUAGES.find(
            (l) => l.code === fromI18n || fromI18n.startsWith(l.code)
        );
        if (i18nMatch) return i18nMatch.code;
    }
    return "en";
};

function applyRtl(code: LanguageCode) {
    if (typeof document === "undefined") return;
    const dir = isRtl(code) ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", code);
}

export const useLanguageStore = create<LanguageStore>()(
    persist(
        (set) => ({
            language: getInitialLanguage(),
            setLanguage: (code) => {
                i18n.changeLanguage(code);
                applyRtl(code);
                if (typeof localStorage !== "undefined") {
                    localStorage.setItem(I18N_LANG_KEY, code);
                }
                set({ language: code });
            },
        }),
        {
            name: PERSIST_KEY,
            // This custom storage adapter works in raw JSON strings (it needs to read/write
            // I18N_LANG_KEY alongside the persisted blob), so it must be wrapped with
            // createJSONStorage to satisfy zustand's PersistStorage<S> (parsed-object) type —
            // passing it directly as `storage` here mismatches getItem's declared return type.
            storage: createJSONStorage(() => ({
                getItem: (name) => {
                    const str = localStorage.getItem(name);
                    if (!str) {
                        const code = localStorage.getItem(I18N_LANG_KEY);
                        if (code && SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
                            return JSON.stringify({ state: { language: code }, version: 1 });
                        }
                        return null;
                    }
                    try {
                        const parsed = JSON.parse(str);
                        const code = parsed?.state?.language;
                        if (code && SUPPORTED_LANGUAGES.some((l) => l.code === code)) {
                            return str;
                        }
                    } catch {
                        /* ignore */
                    }
                    return null;
                },
                setItem: (name, value) => {
                    const str = typeof value === "string" ? value : JSON.stringify(value);
                    localStorage.setItem(name, str);
                    const parsed = typeof value === "string" ? JSON.parse(value) : value;
                    const code = parsed?.state?.language;
                    if (code) localStorage.setItem(I18N_LANG_KEY, String(code));
                },
                removeItem: (name) => {
                    localStorage.removeItem(name);
                    localStorage.removeItem(I18N_LANG_KEY);
                },
            })),
        }
    )
);
